import React from 'react';
import { StyleSheet, View } from 'react-native';
import { AppText } from '../AppText';
import Colors from '../../constants/Colors';
import { useTranslation } from '../../providers/TranslationProvider';
import { SiteStatus } from './siteStatus';
import { formatChipDayLabel, formatClockTime } from './siteFormatting';

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  open: { backgroundColor: Colors.siteListPaleGreenBg },
  closed: { backgroundColor: Colors.lightGrayBackground },
  dot: {
    width: 9,
    height: 9,
    borderRadius: 5,
  },
  openDot: { backgroundColor: Colors.primaryColor },
  closedDot: { backgroundColor: Colors.siteListClosedDot },
  text: {
    fontSize: 16,
    fontWeight: '700',
  },
  openText: { color: Colors.darkGreen },
  closedText: { color: Colors.veryDarkGray },
});

export interface SiteStatusPillProps {
  status: Exclude<SiteStatus, { kind: 'unconfirmed' }>;
  now: Date;
  timezone?: string;
}

export function SiteStatusPill({ status, now, timezone }: SiteStatusPillProps) {
  const { t, locale } = useTranslation();
  const isOpen = status.kind === 'open';

  let label: string;
  if (status.kind === 'open') {
    // closesAt is omitted when the currently-open windows span more than one booking mode --
    // naming a specific time in that case could point to a different event's (e.g.
    // reservation-only) closing time than the one a neighbor can actually use right now.
    label = status.closesAt
      ? t('site_list_status_open_until', { time: formatClockTime(status.closesAt, timezone, locale) })
      : t('site_list_status_open_now');
  } else if (status.kind === 'closedWithNext') {
    const day = formatChipDayLabel(
      status.opensAt,
      now,
      t('site_list_today'),
      t('site_list_tomorrow'),
      timezone,
      locale
    );
    label = t('site_list_status_closed_next_open', {
      day,
      time: formatClockTime(status.opensAt, timezone, locale),
    });
  } else if (status.kind === 'closedMoreAvailable') {
    label = t('site_list_status_more_available');
  } else {
    label = t('site_list_status_no_upcoming');
  }

  return (
    <View style={[styles.pill, isOpen ? styles.open : styles.closed]} testID="site-status-pill">
      <View style={[styles.dot, isOpen ? styles.openDot : styles.closedDot]} />
      <AppText style={[styles.text, isOpen ? styles.openText : styles.closedText]}>{label}</AppText>
    </View>
  );
}
